/** @module Auth0Utils */
/* eslint-disable import/extensions */
import Auth0UtilsPureJs from './auth0-utils-purejs.mjs';
import Auth0CookieHelper from './auth0-cookie-helper.mjs';
import Auth0Constants from './auth0-constants.mjs';
import { getProdCustomDomainPrefixes } from '../config/auth0-tenant-registry.mjs';

class Auth0UtilsBrowser extends Auth0UtilsPureJs {
  static get isPopupLoginWindow() {
    return window.opener && window.opener !== window;
  }

  static get locationWithNoSearchParams() {
    return `${window.location.origin}${window.location.pathname}`;
  }

  static isDropin = () => {
    return document.cookie.indexOf('x-imp=') >= 0;
  };

  static isLocalDevEnv = () => {
    return window.location.href.match(/^http[s]?:\/\/.*local.*.(shipstation|sslocal)\.com/ig);
  };

  /**
   * Determines if the current environment is production based on the window location.
   * Production domains use bare prefixes (e.g., signin.shipstation.com, partners-signin.shipstation.com).
   * Non-prod domains contain environment suffixes like -stage, -dev, -local, etc.
   * @returns {boolean} True if the current environment is production
   */
  static isProdFromCurrentDomain = () => {
    if (typeof window === 'undefined') return false;
    const { hostname } = window.location;
    // Non-prod domains have suffixes: signin-stage, signin-local-z2, partners-signin-dev, etc.
    const subdomain = hostname.split('.')[0];
    // Get all prod custom domain prefixes from the tenant registry
    const prodPrefixes = getProdCustomDomainPrefixes();
    return prodPrefixes.includes(subdomain);
  };

  static get isMobileDevice() {
    return /Mobi|Android|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  static get isIosDevice() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  static get isAndroidDevice() {
    return /Android/i.test(navigator.userAgent);
  }

  static get isIosWebView() {
    const isIos = this.isIosDevice;
    const hasWebkit = !!window.webkit; // Present in both Safari & WebView
    const noSafari = !/Safari/i.test(navigator.userAgent); // WebView omits "Safari"
    const notStandalone = !('standalone' in navigator && navigator.standalone);

    return isIos && hasWebkit && noSafari && notStandalone;
  }

  static amIinSslocalDomain = () => {
    return Auth0UtilsBrowser.urlIndexOf(window.location.hostname, 'sslocal.com') > 0;
  };

  /**
   * Checks whether a URL's hostname shares the current page's base (eTLD+1) domain,
   * e.g. any *.shipstation.com host is trusted from a page also served on *.shipstation.com.
   * This CDN script is only ever served from a domain we control, so window.location's base
   * domain is an inherent trust anchor — nothing an attacker supplies (querystring, appState)
   * can change it. Used to gate post-login targetUrl redirects against cross-domain phishing.
   * @param {string} url The URL to check
   * @returns {boolean} True if url's hostname is the current base domain or a subdomain of it
   */
  static isUrlOnCurrentBaseDomain = (url) => {
    try {
      const targetBaseDomain = Auth0UtilsBrowser.getTopLevelDomain(new URL(url).hostname);
      const currentBaseDomain = Auth0UtilsBrowser.getTopLevelDomain(window.location.hostname);
      return targetBaseDomain === currentBaseDomain;
    } catch {
      return false;
    }
  };

  static parseQueryString = (queryString = null) => {
    queryString = queryString || window.location.search;
    return new URLSearchParams(queryString);
  };

  static parseUrlHash = () => {
    const hash = window.location.hash.substring(1); // Remove the leading '#'
    return new URLSearchParams(hash);
  };

  static openPopup = (w, h, url) => {
    const width = w || 800;
    const height = h || 800;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    return window.open(
      url,
      'auth0:authorize:popup',
      `left=${left},top=${top},width=${width},height=${height},resizable,scrollbars=yes,status=1`,
    );
  };

  static generateNonce = () => {
    if (typeof window.crypto === 'object' && typeof window.crypto.getRandomValues === 'function') {
      const randomBytes = new Uint8Array(16);
      window.crypto.getRandomValues(randomBytes);
      let nonce = '';
      for (let i = 0; i < randomBytes.length; i++) {
        nonce += (`0${randomBytes[i].toString(16)}`).slice(-2);
      }
      return nonce;
    }
    // Crypto API not available, fallback to Math.random
    return Math.random().toString(36).substr(2, 16);
  };

  static dumpVars = (context = 'no context') => {
    const qs = new URLSearchParams(window.location.search);
    console.log(`=== HELPER VAR DUMP (${context}) ================================================`);
    console.log(`qs => ${qs}`);
    console.log(`cookies => ${document.cookie}`);
    console.log('LOCAL STORAGE');
    for (let i = 0; i < localStorage.length; i++) {
      console.log(`=> ${localStorage.key(i)} = ${localStorage.getItem(localStorage.key(i))}`);
    }
    console.log('====================================================================');
  };

  static setOption = (option) => {
    const secInDay = 86400;
    const expirySec = option.expiryDay ? (option.expiryDay * secInDay) : (option.expirySec ?? 30);
    const val = option.stringify ? JSON.stringify(option.value) : option.value;
    console.log(`Auth0UtilsBrowser.setOption => ${option.cookieName} = ${val}`);
    Auth0CookieHelper.setCookie(
      option.cookieName,
      val,
      {
        expiry: Auth0CookieHelper.getExpirationFromSeconds(expirySec),
        allowSplitCookie: option.allowSplitCookie ?? false,
      },
    );
  };

  static getOption = (option, deleteCookie = true, parse = false) => {
    if (typeof option === 'string') {
      option = { keyName: option };
    }

    if (option.keyName) {
      option.searchParamName = option.searchParamName || option.keyName;
      option.cookieName = option.cookieName || option.keyName;
    }

    if (option.cookieName) {
      const cookieHit = Auth0CookieHelper.getCookieValue(option.cookieName);
      if (cookieHit) {
        console.log(`getOption(${option.cookieName}) => ${cookieHit}`);
        if (deleteCookie) {
          Auth0CookieHelper.deleteCookie(option.cookieName);
        }
        if (parse) {
          try {
            // Note: getCookieValue already calls decodeURIComponent, so we just parse directly
            return JSON.parse(cookieHit);
          } catch (parseError) {
            console.error(`getOption: Failed to parse cookie ${option.cookieName}:`, parseError);
            console.error(`getOption: Raw cookie value (${cookieHit.length} chars):`, cookieHit);
            throw parseError;
          }
        }
        return cookieHit;
      }
    }

    // check the searchParam last
    const searchParamHit = Auth0UtilsBrowser.parseQueryString().get(option.searchParamName);
    if (searchParamHit) {
      console.log(`getOption(${option.searchParamName}) => ${searchParamHit}`);
      return searchParamHit;
    }

    return false;
  };

  static replaceCurrentDocument = (htmlContent, onLoaded) => {
    const newHTML = document.open('text/html', 'replace');
    newHTML.write(htmlContent);
    newHTML.close();

    window.addEventListener('load', onLoaded);
  };

  static generateUUID = () => {
    // TODO: implement nodejs version (this is for browsers)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      // eslint-disable-next-line no-bitwise
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 0x0f) | (c === 'x' ? 0 : 0x8);
      return r.toString(16);
    });
  };

  static hideError = (hideErrorDelayMs = 0) => {
    // IMPORTANT: current document must have an element with ID `error-message`
    setTimeout(() => {
      this._hideNotification('error');
    }, hideErrorDelayMs);
  };

  static displayError = (err) => {
    // IMPORTANT: current document must have an element with ID `error-message`
    this._displayNotification(err, 'error');
  };

  static hideInfo = () => {
    // IMPORTANT: current document must have an element with ID `info-message`
    this._hideNotification('info');
  };

  static displayInfo = (info) => {
    // IMPORTANT: current document must have an element with ID `info-message`
    this._displayNotification(info, 'info');
  };

  static extractDisplayTextFromMessage = (msg) => {
    const msgIsString = typeof msg === 'string';
    const msgIsObject = typeof msg === 'object';
    const displayText = msgIsObject && !msgIsString
      ? msg.message || msg.description || msg.error || msg.response?.text || JSON.stringify(msg)
      : msg;

    return displayText;
  };

  static _hideNotification = (msgType) => {
    const msgContainer = document.getElementById(`${msgType}-message`);
    msgContainer.style.display = 'none';
  };

  static _displayNotification = (msg, msgType) => {
    const notificationText = Auth0UtilsBrowser.extractDisplayTextFromMessage(msg);
    console.log(`_displayNotification=>${notificationText}`);
    const msgContainer = document.getElementById(`${msgType}-message`);
    msgContainer.innerHTML = notificationText;
    msgContainer.style.display = 'block';
  };

  static get errorIsVisible() {
    const eleContainer = this.currentErrorElement;
    return eleContainer && eleContainer?.style?.display !== 'none';
  }

  static get infoIsVisible() {
    const eleContainer = this.currentInfoElement;
    return eleContainer && eleContainer?.style?.display !== 'none';
  }

  static get currentErrorText() {
    return this.currentErrorElement.innerText;
  }

  static get currentInfoText() {
    return this.currentErrorElement.innerText;
  }

  static get currentErrorElement() {
    return document.getElementById('error-message');
  }

  static get currentInfoElement() {
    return document.getElementById('info-message');
  }

  static getV3RedirectEquivalentForDev = () => {
    // These redirects are all based on the user's current location
    const currentHostLowerParts = window.location.hostname.split('.');
    currentHostLowerParts[0] = currentHostLowerParts[0]
      .replace(/^ss-dev/i, 'ship-dev')
      .replace(/^v2-dev/i, 'v3-dev')
      .replace(/^dev/i, 'ship-dev')
      .replace(/^ss/i, 'ship');
    return currentHostLowerParts.join('.');
  };

  static specialRedirectFilterForDevEnv = (hostname) => {
    // Special redirect behavior in sslocal.com
    if (Auth0UtilsBrowser.amIinSslocalDomain()) {
      return Auth0UtilsBrowser.isHostnameForV3(hostname) ? Auth0UtilsBrowser.getV3RedirectEquivalentForDev() : window.location.hostname;
    }
    return hostname;
  };

  static encodeForJsCookie = (str) => {
    // encodeURIComponent already preserves: - _ .
    // We only need to restore + which gets encoded as %2B
    return encodeURIComponent(str).replace(/%2B/g, '+');
  };

  static getWorkstationAuthorizationToken = (forUsername, uriEncode = false) => {
    console.log('getWorkstationAuthorizationToken called for username:', forUsername, 'with uriEncode=', uriEncode);
    if (!forUsername) {
      throw new Error('getWorkstationAuthorizationToken must provide a valid username as the first arg');
    }
    const lookupCookieName = `WS_${Auth0UtilsBrowser.encodeForJsCookie(forUsername.toLowerCase())}`;
    console.log(`getWorkstationAuthorizationToken looking for cookie: ${lookupCookieName}`);
    const foundToken = Auth0CookieHelper.getCookieValue(lookupCookieName);
    if (!foundToken) {
      console.log('getWorkstationAuthorizationToken did not find a matching token');
      return;
    }

    const token = uriEncode
      ? encodeURIComponent(foundToken)
      : foundToken;
    console.log('getWorkstationAuthorizationToken found token:', token);

    return token;
  };

  static getAllWorkstationAuthorizationTokens = (uriEncode = false) => {
    let foundTokens = Auth0CookieHelper.getMatchingCookies(/^WS_.+/);

    if (uriEncode) {
      foundTokens = foundTokens.map((token) => { return { name: token.name, value: encodeURIComponent(token.value) }; });
    }
    return foundTokens;
  };

  static setAuth0IsError = (errMsg) => {
    Auth0CookieHelper.setCookie(Auth0Constants.AUTH0_IS_IN_ERROR_STATE, encodeURIComponent(errMsg), { expiry: Auth0CookieHelper.getExpirationFromSeconds(15) });
  };

  /**
   * Triggers a re-login with a newly linked account after successful SSO linking.
   * Sets the AUTH0_CHANNEL_REFRESH cookie to initiate re-authentication with the linked IdP,
   * then redirects to the app login handler.
   *
   * This is used by both user-initiated and admin-initiated linking flows to complete
   * the post-link authentication process.
   *
   * @param {string} idpId - The IdP connection ID (e.g., "google-oauth2", "microsoft-oauth2")
   * @param {string} appLoginHandlerUrl - URL to redirect to for re-login
   * @param {string} [redirectUrl] - Optional final redirect URL after re-login completes
   */
  static triggerReLoginWithLinkedAccount = (idpId, appLoginHandlerUrl, redirectUrl = null) => {
    // Set refresh auth cookie to trigger re-login with linked account
    const refreshOptions = { idpId };
    if (redirectUrl) {
      refreshOptions.redirectUrl = redirectUrl;
    }

    console.log(`triggerReLoginWithLinkedAccount => ${JSON.stringify(refreshOptions)}`);

    // Clear the auth0.is.authenticated cookie so that the consuming app's /login route
    // does not short-circuit on the "already authenticated" check and skip token storage.
    console.log('triggerReLoginWithLinkedAccount => clearing auth0.is.authenticated cookie');
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_AUTHENTICATED);

    // Build the final redirect URL with targetUrl query parameter
    // This ensures the targetUrl is available to the EJS template on the re-login page
    let finalRedirectUrl = appLoginHandlerUrl;
    if (redirectUrl) {
      try {
        const url = new URL(appLoginHandlerUrl, window.location.origin);
        url.searchParams.set('targetUrl', redirectUrl);
        finalRedirectUrl = url.toString();
      } catch (e) {
        console.warn('Failed to append targetUrl to redirect URL:', e);
      }
    }

    // Redirect to app login handler to complete the re-login
    console.log(`Redirecting to ${finalRedirectUrl} to complete re-login with linked account...`);
    window.location.href = finalRedirectUrl;
  };

  static createSVG = (url, size) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('width', size);
    svg.setAttribute('height', size);

    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url);
    image.setAttribute('width', size);
    image.setAttribute('height', size);

    svg.appendChild(image);
    return svg;
  };

  static moveElementToParent = (elementId, targetParentId) => {
    // Get the element to move and the target parent element
    const element = document.getElementById(elementId);
    const targetParent = document.getElementById(targetParentId);

    // Check if both elements exist
    if (element && targetParent) {
      // Remove the element from its current parent
      element.parentNode.removeChild(element);

      // Append the element to the target parent
      targetParent.appendChild(element);
    } else {
      console.error('Element or target parent not found');
    }
  };

  static displayObjectAsTable(obj) {
    // Create the container div
    let container = document.getElementById('bottom-left-table-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'bottom-left-table-container';
      container.style.position = 'fixed';
      container.style.bottom = '0';
      container.style.left = '0';
      container.style.background = 'rgba(255, 255, 255, 0.8)';
      container.style.padding = '10px';
      container.style.fontFamily = 'Arial, sans-serif';
      container.style.fontSize = '14px';
      container.style.zIndex = '1000';
      document.body.appendChild(container);
    }

    // Create the table element
    const table = document.createElement('table');
    table.style.borderCollapse = 'collapse';

    // Populate the table with object properties
    Object.entries(obj).forEach(([key, value]) => {
      const row = document.createElement('tr');

      const keyCell = document.createElement('td');
      keyCell.textContent = `${key}:`;
      keyCell.style.fontWeight = 'bold';
      keyCell.style.paddingRight = '10px';

      const valueCell = document.createElement('td');
      valueCell.innerHTML = value;
      // if the text has warning in it, make it orange (not red, to avoid looking like an error state)
      valueCell.style.color = typeof value === 'string' && value.toLowerCase().includes('warning') ? 'orange' : 'green';

      row.appendChild(keyCell);
      row.appendChild(valueCell);
      table.appendChild(row);
    });

    // Clear previous content and append new table
    container.innerHTML = '';
    container.appendChild(table);
  }
}

export default Auth0UtilsBrowser;
